/**
 * Félbehagyott rögzítések életciklusa.
 *
 * Az ingyenes/alap helyreállítás ugyanazon az eszközön, IndexedDB-ből működik.
 * Egy későbbi Pro „folytatás bármikor, másik eszközön is” külön, tartós
 * felhős formátum lesz; nem ennek az időablaknak a kitágítása. A mostani
 * felhős tracking dokumentum ritkított kijelzési snapshot, nem hiteles mentés.
 */

/** Az alapcsomagban eddig folytatható egy megszakadt helyi rögzítés. */
export const BASIC_RESUME_WINDOW_MS = 60 * 60 * 1000;

export function isInsideBasicResumeWindow(savedAt: number, now: number): boolean {
  return Number.isFinite(savedAt)
    && savedAt > 0
    && now - savedAt <= BASIC_RESUME_WINDOW_MS;
}

/**
 * Ennyi ideig hallgathat el a JS-kontextus úgy, hogy azt még ÁRTALMATLAN
 * natív WebView-újraindulásnak vesszük, nem tényleges megszakításnak.
 *
 * A Core Location/foreground service a háttérben zökkenőmentesen tovább
 * mérhet, amíg maga a WebView memóriaszűke miatt újratöltődik — ez a
 * felhasználó szemszögéből SOSEM történt meg, tehát a folytatásnak is
 * kérdés nélkül, azonnal kell történnie.
 *
 * Ennél hosszabb kihagyásnál viszont már nem tartható, hogy csak egy ilyen
 * ártalmatlan reload volt: valószínűbb, hogy a felhasználó ténylegesen
 * háttérbe tette vagy be is zárta az appot (esetleg véletlen force-quit),
 * és erről MEG KELL KÉRDEZNI — lásd GRUNDO #42: a natív oldal korábban
 * minden ilyen esetben kérdés nélkül, láthatatlanul folytatta a rögzítést.
 */
export const NATIVE_SILENT_RESUME_WINDOW_MS = 2 * 60 * 1000;

export function isWithinSilentNativeResumeWindow(savedAt: number, now: number): boolean {
  return Number.isFinite(savedAt) && savedAt > 0 && now - savedAt <= NATIVE_SILENT_RESUME_WINDOW_MS;
}

/**
 * Ennél frissebb utolsó natív fix mellett a natív mérés MOST is fut.
 * Álló helyzetben a natív szűrő nem rögzít, a kor nő — ilyenkor a bizonyíték
 * „ismeretlen”, és a régi időszabály dönt (kétes esetben kérdés).
 */
export const NATIVE_ALIVE_MAX_FIX_AGE_MS = 2 * 60 * 1000;

/** Amit a natív diagnosztikából a folytatási döntéshez felhasználunk. */
export interface NativeSessionDiagnostics {
  platform: 'ios' | 'android';
  updating: boolean;
  /** Android: a szolgáltatás szakadatlanul futott (memóriabeli bizonyíték). */
  serviceAlive?: boolean;
  /** iOS (#80): a kilövés utáni háttér-folytatás fut — lásd `nativeSessionEvidence`. */
  resumerActive?: boolean;
  lastFixAgeMs?: number;
}

export type NativeSessionEvidence = 'alive' | 'dead' | 'unknown';

/**
 * Fut-e MÉG a natív mérés, ami a WebView halála előtt indult? (GRUNDO #72,
 * Geri döntése: a mért bizonyíték dönt, nem az eltelt idő.)
 *
 * - iOS: az `updating` és a `lastFixAgeMs` MEMÓRIÁBAN él — a folyamat halálával
 *   (felhúzásos bezárás, rendszer általi kilövés) elvész. Ha igaz és friss, a
 *   folyamat túlélte, tehát a mérés a kiesés alatt sem állt le.
 * - Android: az `updating` egy TARTÓSAN mentett jelző (`SharedPreferences`),
 *   ami a folyamat halálát is túléli — önmagában NEM bizonyíték. A `serviceAlive`
 *   memóriabeli: igaz, ha a szolgáltatás ebben a folyamatban fut és nem a
 *   rendszer indította újra kilövés után. Hiányzik (régi natív build) → `unknown`.
 * - Hiányzó diagnosztika (régi natív build, hiba): `unknown`.
 */
export function nativeSessionEvidence(
  diagnostics: NativeSessionDiagnostics | null,
): NativeSessionEvidence {
  if (diagnostics === null) return 'unknown';
  /*
    #80: az iOS a kilőtt appot háttérben újraindította, és a mérés folytatódott
    (`GrundoRecordingResumer`). Ez a futó folyamat bizonyítja, hogy a mérés nem
    állt le — akkor is, ha álló helyzetben régóta nem jött fix.
  */
  if (diagnostics.platform === 'ios' && diagnostics.resumerActive) return 'alive';
  if (!diagnostics.updating) return diagnostics.platform === 'ios' ? 'dead' : 'unknown';
  if (diagnostics.platform === 'android') {
    if (diagnostics.serviceAlive === undefined) return 'unknown';
    if (!diagnostics.serviceAlive) return 'dead';
  }
  const age = diagnostics.lastFixAgeMs;
  if (age === undefined || !Number.isFinite(age) || age > NATIVE_ALIVE_MAX_FIX_AGE_MS) return 'unknown';
  return 'alive';
}

/**
 * Másik eszköz pillanatképe csak élő/szünetelő állapotban és legfeljebb egy
 * órán át jelenhet meg. A `finished` nem félbehagyott út, azonnal eltűnik.
 */
export function isRemoteTrackingVisible(
  status: string,
  updatedAt: number,
  now: number,
): boolean {
  return (status === 'recording' || status === 'paused')
    && isInsideBasicResumeWindow(updatedAt, now);
}
